import { useEffect, useState } from "react";
import { api } from "../lib/apiClient";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "./ui/dialog";
import { Input } from "./ui/input";
import { Gift, Trash2, Plus, CalendarClock } from "lucide-react";

const STATE_STYLES = {
  active: "bg-[#E4EDE4] text-[#506B52]",
  scheduled: "bg-[#EEE7DC] text-[#8C7154]",
  expired: "bg-[#F2EFEB] text-[#9b938b]",
};

const fmtDate = (d) => {
  try { return new Date(d + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }); }
  catch { return d; }
};

export default function CompGrantsDialog({ open, onOpenChange }) {
  const [grants, setGrants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState("");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/admin/comp-grants").then((r) => setGrants(r.data.grants || [])).catch(() => {}).finally(() => setLoading(false));
  };

  useEffect(() => { if (open) load(); }, [open]);

  const submit = async (e) => {
    e.preventDefault();
    if (!email || !start || !end) { toast.error("Email, start date and end date are all required."); return; }
    setBusy(true);
    try {
      await api.post("/admin/comp-grants", { email, start_date: start, end_date: end, note });
      toast.success(`Free membership set for ${email.toLowerCase()}.`);
      setEmail(""); setStart(""); setEnd(""); setNote("");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save the free membership.");
    } finally { setBusy(false); }
  };

  const revoke = async (g) => {
    try {
      await api.delete(`/admin/comp-grants/${g.id}`);
      toast.success(`Removed free membership for ${g.email}.`);
      load();
    } catch { toast.error("Could not remove that grant."); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="comp-grants-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl text-[#2B2622] flex items-center gap-2">
            <Gift size={20} className="text-[#A68A6B]" /> Free Memberships
          </DialogTitle>
          <DialogDescription>
            Grant card-free access to a designer by email for a set period. During the period they skip the paywall entirely.
            Once it ends, they'll be asked to subscribe to continue. Everyone else still starts with the standard 7-day card trial.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="grid grid-cols-2 gap-3 border border-[#EBE5E0] p-4 bg-[#FBFAF8]">
          <div className="col-span-2">
            <label className="text-[11px] uppercase tracking-wider text-[#A68A6B]">Designer email</label>
            <Input data-testid="comp-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
              placeholder="designer@example.com"
              className="mt-1 rounded-none border-[#EBE5E0] focus-visible:ring-0 focus-visible:border-[#A68A6B] text-sm" />
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-wider text-[#A68A6B]">Start date</label>
            <Input data-testid="comp-start-input" type="date" value={start} onChange={(e) => setStart(e.target.value)}
              className="mt-1 rounded-none border-[#EBE5E0] focus-visible:ring-0 focus-visible:border-[#A68A6B] text-sm" />
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-wider text-[#A68A6B]">End date</label>
            <Input data-testid="comp-end-input" type="date" value={end} onChange={(e) => setEnd(e.target.value)}
              className="mt-1 rounded-none border-[#EBE5E0] focus-visible:ring-0 focus-visible:border-[#A68A6B] text-sm" />
          </div>
          <div className="col-span-2">
            <label className="text-[11px] uppercase tracking-wider text-[#A68A6B]">Note (optional)</label>
            <Input data-testid="comp-note-input" value={note} onChange={(e) => setNote(e.target.value)}
              placeholder="e.g. Launch partner, 1 free month"
              className="mt-1 rounded-none border-[#EBE5E0] focus-visible:ring-0 focus-visible:border-[#A68A6B] text-sm" />
          </div>
          <div className="col-span-2">
            <button data-testid="comp-submit-btn" type="submit" disabled={busy}
              className="inline-flex items-center gap-2 bg-[#2B2622] hover:bg-[#A68A6B] text-white uppercase tracking-wider text-xs py-3 px-5 transition-colors disabled:opacity-60">
              <Plus size={15} /> {busy ? "Saving…" : "Grant free membership"}
            </button>
          </div>
        </form>

        <div className="max-h-72 overflow-y-auto border border-[#EBE5E0]">
          {loading ? (
            <p className="p-4 text-sm text-[#5C5651]">Loading…</p>
          ) : grants.length === 0 ? (
            <p className="p-4 text-sm text-[#9b938b]" data-testid="comp-empty">No free memberships yet.</p>
          ) : (
            <table className="w-full text-left text-sm" data-testid="comp-grants-table">
              <thead>
                <tr className="text-[10px] uppercase tracking-wider text-[#A68A6B] border-b border-[#EBE5E0]">
                  <th className="p-3 font-normal">Email</th>
                  <th className="p-3 font-normal">Period</th>
                  <th className="p-3 font-normal">Status</th>
                  <th className="p-3 font-normal text-right"></th>
                </tr>
              </thead>
              <tbody>
                {grants.map((g) => (
                  <tr key={g.id} className="border-b border-[#F2EFEB] last:border-0" data-testid={`comp-row-${g.id}`}>
                    <td className="p-3 text-[#2B2622]">{g.email}{g.note ? <span className="block text-[11px] text-[#9b938b]">{g.note}</span> : null}</td>
                    <td className="p-3 text-[#5C5651] whitespace-nowrap"><CalendarClock size={12} className="inline mr-1 text-[#C9BBAE]" />{fmtDate(g.start_date)} – {fmtDate(g.end_date)}</td>
                    <td className="p-3">
                      <span className={`inline-block px-2 py-0.5 text-[10px] uppercase tracking-wider ${STATE_STYLES[g.state] || "bg-[#F2EFEB] text-[#5C5651]"}`}>{g.state}</span>
                    </td>
                    <td className="p-3 text-right">
                      <button data-testid={`comp-revoke-${g.id}`} onClick={() => revoke(g)}
                        className="inline-flex items-center gap-1 text-[#9E4747] hover:text-white hover:bg-[#9E4747] px-2 py-1 text-[11px] uppercase tracking-wider transition-colors">
                        <Trash2 size={13} /> Revoke
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
